import React from 'react';

import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';
import type { GroupBuying } from '@/types/database/groupBuying';
import type { GroupBuyingOrders } from '@/types/database/groupBuyingOrders';
import type { User } from '@/types/database/users';

import OrdersList from '../OrdersList';
import type { GroupBuyingOrdersContextValues } from '../types';

export const tabsItems = [
  {
    label: '現在團購',
    key: '現在團購',
    children: <OrdersList listType="現在團購" />,
  },
  {
    label: '歷史資料',
    key: '歷史資料',
    children: <OrdersList listType="歷史資料" />,
  },
];

export const groupBuyingOrdersContextDefaultValues: GroupBuyingOrdersContextValues =
  {
    currentOrders: [],
    historyOrders: [],
    updateOrders: () => {},
  };

const checkIsCurrentOrder = (args: {
  groupBuyingItems: GroupBuying[];
  orders: GroupBuyingOrders[];
  order: GroupBuyingOrders;
}): GroupBuyingOrders[] => {
  const { groupBuyingItems, orders, order } = args;

  groupBuyingItems.forEach((item) => {
    if (item._id === order.groupBuyingId && item.status === '待成團') {
      orders.push(order);
    }
  });
  return orders;
};

export const getCurrentOrders = (
  groupBuyingOrders: GroupBuyingOrders[],
  groupBuyingItems: GroupBuying[]
) => {
  const currentOrders = groupBuyingOrders.reduce(
    (orders: GroupBuyingOrders[], order) => {
      const checkedOrders = checkIsCurrentOrder({
        groupBuyingItems,
        orders,
        order,
      });
      return checkedOrders;
    },
    []
  );

  return currentOrders;
};

const checkIsHistoryOrders = (args: {
  groupBuyingItems: GroupBuying[];
  orders: GroupBuyingOrders[];
  order: GroupBuyingOrders;
}): GroupBuyingOrders[] => {
  const { groupBuyingItems, orders, order } = args;

  groupBuyingItems.forEach((item) => {
    if (item._id === order.groupBuyingId && item.status !== '待成團') {
      orders.push(order);
    }
  });
  return orders;
};

export const getHistoryOrders = (
  groupBuyingOrders: GroupBuyingOrders[],
  groupBuyingItems: GroupBuying[]
) => {
  const historyOrders = groupBuyingOrders.reduce(
    (orders: GroupBuyingOrders[], order) => {
      const checkedOrders = checkIsHistoryOrders({
        groupBuyingItems,
        orders,
        order,
      });
      return checkedOrders;
    },
    []
  );

  return historyOrders;
};

const filterOrderByItemName = (args: {
  groupBuyingItems: GroupBuying[];
  orders: GroupBuyingOrders[];
  order: GroupBuyingOrders;
  itemName: string;
}): GroupBuyingOrders[] => {
  const { groupBuyingItems, orders, order, itemName } = args;

  groupBuyingItems.forEach((item) => {
    if (item.itemName === itemName && item._id === order.groupBuyingId) {
      orders.push(order);
    }
  });
  return orders;
};

export const getFilteredOrdersByItemName = (args: {
  groupBuyingOrders: GroupBuyingOrders[];
  groupBuyingItems: GroupBuying[];
  itemName: string;
}) => {
  const { groupBuyingOrders, groupBuyingItems, itemName } = args;

  const filteredOrdersByItemName = groupBuyingOrders.reduce(
    (orders: GroupBuyingOrders[], order) => {
      const filteredOrders = filterOrderByItemName({
        groupBuyingItems,
        orders,
        order,
        itemName,
      });
      return filteredOrders;
    },
    []
  );

  return filteredOrdersByItemName;
};

const filterOrderByHouseholdNumber = (args: {
  users: User[];
  orders: GroupBuyingOrders[];
  order: GroupBuyingOrders;
  householdNumber: string[];
}): GroupBuyingOrders[] => {
  const { users, orders, order, householdNumber } = args;

  users.forEach((user) => {
    if (
      order.userId === user._id &&
      user.householdNumbers &&
      user.householdNumbers[0] === householdNumber?.join('-')
    ) {
      orders.push(order);
    }
  });
  return orders;
};

export const getFilteredOrdersByHouseholdNumber = (args: {
  users: User[];
  groupBuyingOrders: GroupBuyingOrders[];
  householdNumber: string[];
}) => {
  const { users, groupBuyingOrders, householdNumber } = args;

  const filteredOrdersByHouseholdNumber = groupBuyingOrders.reduce(
    (orders: GroupBuyingOrders[], order) => {
      const filteredOrders = filterOrderByHouseholdNumber({
        users,
        orders,
        order,
        householdNumber,
      });

      return filteredOrders;
    },
    []
  );

  return filteredOrdersByHouseholdNumber;
};

const getHouseholdNumbersForOrders = (args: {
  users: User[];
  order: GroupBuyingOrders;
  householdNumbers: string[];
}): string[] => {
  const { users, order, householdNumbers } = args;

  users.forEach((user) => {
    if (user.householdNumbers && user._id === order.userId) {
      householdNumbers.push(user.householdNumbers[0]);
    }
  });
  return householdNumbers;
};

export const getCurrentOrderHouseholdNumbers = (args: {
  users: User[];
  groupBuyingOrders: GroupBuyingOrders[];
  groupBuyingItems: GroupBuying[];
}): string[] => {
  const { users, groupBuyingOrders, groupBuyingItems } = args;

  const currentOrders = getCurrentOrders(groupBuyingOrders, groupBuyingItems);

  const currentOrdersHouseholdNumbers = currentOrders.reduce(
    (householdNumbers: string[], order) => {
      const householdNumbersForOrders = getHouseholdNumbersForOrders({
        users,
        order,
        householdNumbers,
      });
      return householdNumbersForOrders;
    },
    []
  );

  return currentOrdersHouseholdNumbers;
};

export const getHistoryOrderHouseholdNumbers = (args: {
  users: User[];
  groupBuyingOrders: GroupBuyingOrders[];
  groupBuyingItems: GroupBuying[];
}) => {
  const { users, groupBuyingOrders, groupBuyingItems } = args;

  const historyOrders = getHistoryOrders(groupBuyingOrders, groupBuyingItems);
  const historyOrdersHouseholdNumbers = historyOrders.reduce(
    (householdNumbers: string[], order) => {
      const householdNumbersForOrders = getHouseholdNumbersForOrders({
        users,
        order,
        householdNumbers,
      });
      return householdNumbersForOrders;
    },
    []
  );

  return historyOrdersHouseholdNumbers;
};

export const getCascaderOptions = (args: {
  tab: string;
  users: User[];
  groupBuyingOrders: GroupBuyingOrders[];
  groupBuyingItems: GroupBuying[];
}) => {
  const { tab, users, groupBuyingOrders, groupBuyingItems } = args;

  if (tab === '現在團購') {
    const currentOrderHouseholdNumbers = getCurrentOrderHouseholdNumbers({
      users,
      groupBuyingOrders,
      groupBuyingItems,
    });
    const cascaderOptions = generateHouseHoldNumberCascader(
      currentOrderHouseholdNumbers
    );
    return cascaderOptions;
  } else {
    const historyOrderHouseholdNumbers = getHistoryOrderHouseholdNumbers({
      users,
      groupBuyingOrders,
      groupBuyingItems,
    });
    const cascaderOptions = generateHouseHoldNumberCascader(
      historyOrderHouseholdNumbers
    );
    return cascaderOptions;
  }
};
